/**
 * Dental Chart Constants and SVG Path Data
 * Contains tooth metadata, SVG paths, and condition definitions
 * Follows FDI (Fédération Dentaire Internationale) numbering system
 */

// Tooth condition colors
export const TOOTH_CONDITIONS = {
    healthy: {
        color: '#FFFFFF',
        borderColor: '#52c41a',
        label: 'tooth:healthy',
        icon: '✓'
    },
    cavity: {
        color: '#FF6B6B',
        borderColor: '#e74c3c',
        label: 'tooth:cavity',
        icon: '⚠'
    },
    filled: {
        color: '#4ECDC4',
        borderColor: '#16a085',
        label: 'tooth:filled',
        icon: '▣'
    },
    missing: {
        color: '#95A5A6',
        borderColor: '#7f8c8d',
        label: 'tooth:missing',
        icon: '✗'
    },
    crown: {
        color: '#F39C12',
        borderColor: '#d68910',
        label: 'tooth:crown',
        icon: '♔'
    },
    rootCanal: {
        color: '#9B59B6',
        borderColor: '#8e44ad',
        label: 'tooth:rootCanal',
        icon: '⚕'
    },
    implant: {
        color: '#3498DB',
        borderColor: '#2980b9',
        label: 'tooth:implant',
        icon: '⊕'
    },
    bridge: {
        color: '#E67E22',
        borderColor: '#d35400',
        label: 'tooth:bridge',
        icon: '⌒'
    },
    extracted: {
        color: '#BDC3C7',
        borderColor: '#95a5a6',
        label: 'tooth:extracted',
        icon: '∅'
    }
};

// SVG Tooth Paths - Real Anatomically Accurate Human Teeth
export const TOOTH_SVG_PATHS = {
    // Central and Lateral Incisors (actual human tooth shape)
    incisor: {
        crown: `
            M 11 2.5
            C 11.2 2.1 12.5 1.5 13.5 1.2
            C 14.3 1 15.5 1 16.5 1.2
            C 17.5 1.5 18.8 2.1 19 2.5
            L 19.5 4.5
            C 19.8 6.2 19.9 7.5 19.7 9
            C 19.5 10.2 19.2 11.5 18.5 12.3
            L 17.5 13.5
            C 17 14 16.2 14.5 15.5 14.7
            L 14.5 14.7
            C 13.8 14.5 13 14 12.5 13.5
            L 11.5 12.3
            C 10.8 11.5 10.5 10.2 10.3 9
            C 10.1 7.5 10.2 6.2 10.5 4.5
            L 11 2.5 Z
            M 12.5 4
            L 12.2 6.5
            C 12 7.8 12.2 9.5 12.8 10.5
            C 13.2 11.2 13.8 12 14.5 12.5
            L 15.5 12.5
            C 16.2 12 16.8 11.2 17.2 10.5
            C 17.8 9.5 18 7.8 17.8 6.5
            L 17.5 4
            C 16.5 3.2 15.5 3 14.5 3
            C 13.5 3 12.5 3.2 12.5 4 Z
        `,
        dentin: `
            M 13 5
            L 12.8 7
            C 12.7 8 12.8 9.2 13.2 10
            C 13.5 10.5 14 11 14.5 11.2
            L 15.5 11.2
            C 16 11 16.5 10.5 16.8 10
            C 17.2 9.2 17.3 8 17.2 7
            L 17 5
            C 16.5 4.5 15.8 4.2 15 4.2
            C 14.2 4.2 13.5 4.5 13 5 Z
        `,
        pulp: `
            M 14 6.5
            L 13.9 8
            C 13.9 8.5 14 9 14.3 9.3
            L 14.5 9.5
            L 15.5 9.5
            L 15.7 9.3
            C 16 9 16.1 8.5 16.1 8
            L 16 6.5
            C 15.7 6.2 15.3 6 15 6
            C 14.7 6 14.3 6.2 14 6.5 Z
        `,
        root: `
            M 14 14.7
            L 14.5 14.8
            L 15 14.8
            L 16 14.7
            C 16.2 15.5 16.3 16.5 16.2 17.8
            L 16 20
            C 15.8 22 15.5 24 15.3 25.5
            L 15 27
            L 14.5 25.5
            C 14.3 24 14 22 13.8 20
            L 13.6 17.8
            C 13.5 16.5 13.6 15.5 13.8 14.7
            L 14 14.7 Z
        `
    },

    // Canine (pointed tooth - actual human canine shape)
    canine: {
        crown: `
            M 15 1
            C 13.5 1.2 12 2 11 3.5
            L 10 5.5
            C 9.5 7 9.2 8.5 9.2 10
            L 9.2 12.5
            C 9.3 14 9.7 15.2 10.5 16
            L 11.5 17
            C 12.2 17.5 13.2 18 14.5 18.2
            L 15.5 18.2
            C 16.8 18 17.8 17.5 18.5 17
            L 19.5 16
            C 20.3 15.2 20.7 14 20.8 12.5
            L 20.8 10
            C 20.8 8.5 20.5 7 20 5.5
            L 19 3.5
            C 18 2 16.5 1.2 15 1 Z
            M 12 4.5
            L 11 7
            C 10.7 8.5 10.5 10 10.8 11.5
            C 11 12.8 11.5 14 12.5 14.8
            L 14 15.8
            L 16 15.8
            L 17.5 14.8
            C 18.5 14 19 12.8 19.2 11.5
            C 19.5 10 19.3 8.5 19 7
            L 18 4.5
            C 17 3.5 16 3 15 3
            C 14 3 13 3.5 12 4.5 Z
        `,
        dentin: `
            M 13 5.5
            L 12.2 7.5
            C 12 8.8 11.9 10 12.1 11.2
            C 12.3 12.2 12.7 13 13.5 13.5
            L 14.5 14.2
            L 15.5 14.2
            L 16.5 13.5
            C 17.3 13 17.7 12.2 17.9 11.2
            C 18.1 10 18 8.8 17.8 7.5
            L 17 5.5
            C 16.3 4.8 15.5 4.5 15 4.5
            C 14.5 4.5 13.7 4.8 13 5.5 Z
        `,
        pulp: `
            M 14.2 7.5
            L 14 9
            C 14 9.8 14.1 10.5 14.3 11
            L 14.7 11.8
            L 15 12
            L 15.3 11.8
            L 15.7 11
            C 15.9 10.5 16 9.8 16 9
            L 15.8 7.5
            C 15.5 7 15.2 6.8 15 6.8
            C 14.8 6.8 14.5 7 14.2 7.5 Z
        `,
        root: `
            M 14 18.2
            L 14.5 18.3
            L 15.5 18.3
            L 16 18.2
            C 16.2 19.5 16.2 21 16 22.5
            L 15.7 25
            C 15.5 26.5 15.3 27.8 15 29
            C 14.7 27.8 14.5 26.5 14.3 25
            L 14 22.5
            C 13.8 21 13.8 19.5 14 18.2 Z
        `
    },

    // Premolars (bicuspids with two cusps - realistic side view)
    premolar: {
        crown: `
            M 8.5 4.5
            C 9 3.5 10 2.8 11.5 2.3
            C 12.5 2 13.5 1.8 14.5 1.8
            C 15.5 1.8 16.5 2 17.5 2.3
            C 19 2.8 20 3.5 20.5 4.5
            C 21.2 5.5 21.5 6.5 21.7 7.5
            C 21.8 8.2 21.8 8.8 21.5 9.5
            C 21.8 10.2 21.8 10.8 21.7 11.5
            C 21.5 12.5 21.2 13.5 20.5 14.5
            C 19.8 15.5 18.8 16.3 17.5 17
            C 16.5 17.5 15.5 17.8 14.5 17.8
            C 13.5 17.8 12.5 17.5 11.5 17
            C 10.2 16.3 9.2 15.5 8.5 14.5
            C 7.8 13.5 7.5 12.5 7.3 11.5
            C 7.2 10.8 7.2 10.2 7.5 9.5
            C 7.2 8.8 7.2 8.2 7.3 7.5
            C 7.5 6.5 7.8 5.5 8.5 4.5 Z
            M 11 5.5
            C 11.8 4.8 12.8 4.3 14 4.2
            C 15.2 4.3 16.2 4.8 17 5.5
            C 17.5 6.2 17.8 7 18 8
            C 18.2 8.5 18.2 9 18 9.5
            C 18.2 10 18.2 10.5 18 11
            C 17.8 12 17.5 12.8 17 13.5
            C 16.2 14.2 15.2 14.7 14 14.8
            C 12.8 14.7 11.8 14.2 11 13.5
            C 10.5 12.8 10.2 12 10 11
            C 9.8 10.5 9.8 10 10 9.5
            C 9.8 9 9.8 8.5 10 8
            C 10.2 7 10.5 6.2 11 5.5 Z
        `,
        dentin: `
            M 10.5 6.5
            C 11.2 5.8 12.2 5.3 13.5 5.2
            C 14.8 5.3 15.8 5.8 16.5 6.5
            C 17 7.2 17.3 8 17.5 9
            C 17.6 9.5 17.6 10 17.5 10.5
            C 17.3 11.5 17 12.3 16.5 13
            C 15.8 13.7 14.8 14.2 13.5 14.3
            C 12.2 14.2 11.2 13.7 10.5 13
            C 10 12.3 9.7 11.5 9.5 10.5
            C 9.4 10 9.4 9.5 9.5 9
            C 9.7 8 10 7.2 10.5 6.5 Z
        `,
        pulp: `
            M 12.5 8.5
            C 12.8 8 13.2 7.8 13.5 7.8
            C 13.8 7.8 14.2 8 14.5 8.5
            C 14.8 9 15 9.5 15 10
            C 15 10.5 14.8 11 14.5 11.5
            C 14.2 12 13.8 12.2 13.5 12.2
            C 13.2 12.2 12.8 12 12.5 11.5
            C 12.2 11 12 10.5 12 10
            C 12 9.5 12.2 9 12.5 8.5 Z
        `,
        root: `
            M 12.5 17.8
            C 12.7 18 13 18.2 13.5 18.3
            C 13.7 19.5 13.8 21 13.6 22.5
            C 13.4 24 13.2 25.3 13 26.5
            C 12.8 27 12.7 27.3 12.5 27.5
            C 12.3 27.3 12.2 27 12 26.5
            C 11.8 25.3 11.6 24 11.4 22.5
            C 11.2 21 11.3 19.5 11.5 18.3
            C 12 18.2 12.3 18 12.5 17.8 Z
            M 14.5 17.8
            C 14.7 18 15 18.2 15.5 18.3
            C 15.7 19.5 15.8 21 15.6 22.5
            C 15.4 24 15.2 25.3 15 26.5
            C 14.8 27 14.7 27.3 14.5 27.5
            C 14.3 27.3 14.2 27 14 26.5
            C 13.8 25.3 13.6 24 13.4 22.5
            C 13.2 21 13.3 19.5 13.5 18.3
            C 14 18.2 14.3 18 14.5 17.8 Z
        `
    },

    // Molars (large grinding teeth with multiple cusps - realistic side view)
    molar: {
        crown: `
            M 7 5.5
            C 7.5 4.5 8.5 3.5 10 2.8
            C 11.5 2.2 13 1.8 14.5 1.8
            C 16 1.8 17.5 2.2 19 2.8
            C 20.5 3.5 21.5 4.5 22 5.5
            C 22.8 6.5 23.2 7.5 23.5 8.5
            C 23.7 9.2 23.7 9.8 23.5 10.5
            C 23.7 11.2 23.7 11.8 23.5 12.5
            C 23.2 13.5 22.8 14.5 22 15.5
            C 21.2 16.5 20 17.5 18.5 18.2
            C 17.5 18.7 16 19 14.5 19
            C 13 19 11.5 18.7 10.5 18.2
            C 9 17.5 7.8 16.5 7 15.5
            C 6.2 14.5 5.8 13.5 5.5 12.5
            C 5.3 11.8 5.3 11.2 5.5 10.5
            C 5.3 9.8 5.3 9.2 5.5 8.5
            C 5.8 7.5 6.2 6.5 7 5.5 Z
            M 9 7.5
            C 9.5 6.5 10.5 5.8 11.5 5.3
            C 12.5 5 13.5 4.8 14.5 4.8
            C 15.5 4.8 16.5 5 17.5 5.3
            C 18.5 5.8 19.5 6.5 20 7.5
            C 20.5 8.2 20.8 9 21 10
            C 21.2 10.5 21.2 11 21 11.5
            C 21.2 12 21.2 12.5 21 13
            C 20.8 14 20.5 14.8 20 15.5
            C 19.5 16.2 18.5 16.7 17.5 17
            C 16.5 17.3 15.5 17.5 14.5 17.5
            C 13.5 17.5 12.5 17.3 11.5 17
            C 10.5 16.7 9.5 16.2 9 15.5
            C 8.5 14.8 8.2 14 8 13
            C 7.8 12.5 7.8 12 8 11.5
            C 7.8 11 7.8 10.5 8 10
            C 8.2 9 8.5 8.2 9 7.5 Z
            M 11.5 9
            C 11.8 8.5 12.2 8.2 12.5 8.2
            C 12.8 8.2 13.2 8.5 13.5 9
            C 13.7 9.5 13.8 10 13.8 10.5
            C 13.8 11 13.7 11.5 13.5 12
            C 13.2 12.5 12.8 12.8 12.5 12.8
            C 12.2 12.8 11.8 12.5 11.5 12
            C 11.3 11.5 11.2 11 11.2 10.5
            C 11.2 10 11.3 9.5 11.5 9 Z
            M 15.5 9
            C 15.8 8.5 16.2 8.2 16.5 8.2
            C 16.8 8.2 17.2 8.5 17.5 9
            C 17.7 9.5 17.8 10 17.8 10.5
            C 17.8 11 17.7 11.5 17.5 12
            C 17.2 12.5 16.8 12.8 16.5 12.8
            C 16.2 12.8 15.8 12.5 15.5 12
            C 15.3 11.5 15.2 11 15.2 10.5
            C 15.2 10 15.3 9.5 15.5 9 Z
        `,
        dentin: `
            M 10.5 8.5
            C 11 7.5 12 6.8 13 6.3
            C 13.5 6.1 14 6 14.5 6
            C 15 6 15.5 6.1 16 6.3
            C 17 6.8 18 7.5 18.5 8.5
            C 19 9.2 19.3 10 19.5 11
            C 19.6 11.5 19.6 12 19.5 12.5
            C 19.3 13.5 19 14.3 18.5 15
            C 18 15.7 17 16.2 16 16.5
            C 15.5 16.7 15 16.8 14.5 16.8
            C 14 16.8 13.5 16.7 13 16.5
            C 12 16.2 11 15.7 10.5 15
            C 10 14.3 9.7 13.5 9.5 12.5
            C 9.4 12 9.4 11.5 9.5 11
            C 9.7 10 10 9.2 10.5 8.5 Z
        `,
        pulp: `
            M 12.5 10
            C 12.8 9.5 13.2 9.3 13.5 9.3
            C 13.8 9.3 14.2 9.5 14.5 10
            C 14.7 10.5 14.8 11 14.8 11.5
            C 14.8 12 14.7 12.5 14.5 13
            C 14.2 13.5 13.8 13.7 13.5 13.7
            C 13.2 13.7 12.8 13.5 12.5 13
            C 12.3 12.5 12.2 12 12.2 11.5
            C 12.2 11 12.3 10.5 12.5 10 Z
        `,
        root: `
            M 11.5 19
            C 11.7 19.2 12 19.3 12.5 19.5
            C 12.7 20.5 12.8 22 12.6 23.5
            C 12.4 25 12.2 26.3 12 27.5
            C 11.8 28.2 11.7 28.5 11.5 28.8
            C 11.3 28.5 11.2 28.2 11 27.5
            C 10.8 26.3 10.6 25 10.4 23.5
            C 10.2 22 10.3 20.5 10.5 19.5
            C 11 19.3 11.3 19.2 11.5 19 Z
            M 13.5 19
            C 13.7 19.2 14 19.3 14.5 19.5
            C 14.7 21 14.8 22.5 14.7 24
            C 14.6 25.5 14.4 27 14.2 28.2
            C 14 28.8 13.8 29.2 13.5 29.5
            C 13.2 29.2 13 28.8 12.8 28.2
            C 12.6 27 12.4 25.5 12.3 24
            C 12.2 22.5 12.3 21 12.5 19.5
            C 13 19.3 13.3 19.2 13.5 19 Z
            M 15.5 19
            C 15.7 19.2 16 19.3 16.5 19.5
            C 16.7 20.5 16.8 22 16.6 23.5
            C 16.4 25 16.2 26.3 16 27.5
            C 15.8 28.2 15.7 28.5 15.5 28.8
            C 15.3 28.5 15.2 28.2 15 27.5
            C 14.8 26.3 14.6 25 14.4 23.5
            C 14.2 22 14.3 20.5 14.5 19.5
            C 15 19.3 15.3 19.2 15.5 19 Z
        `
    },

    // Third Molar / Wisdom Tooth (similar to molar but more compact and variable)
    wisdomTooth: {
        crown: `
            M 8 6.5
            C 8.5 5.5 9.5 4.5 11 3.8
            C 12.5 3.2 13.5 3 14.5 3
            C 15.5 3 16.5 3.2 18 3.8
            C 19.5 4.5 20.5 5.5 21 6.5
            C 21.8 7.5 22.2 8.5 22.5 9.5
            C 22.7 10.2 22.7 10.8 22.5 11.5
            C 22.7 12.2 22.7 12.8 22.5 13.5
            C 22.2 14.5 21.8 15.5 21 16.5
            C 20.2 17.5 19 18.3 17.5 18.8
            C 16.5 19.2 15.5 19.3 14.5 19.3
            C 13.5 19.3 12.5 19.2 11.5 18.8
            C 10 18.3 8.8 17.5 8 16.5
            C 7.2 15.5 6.8 14.5 6.5 13.5
            C 6.3 12.8 6.3 12.2 6.5 11.5
            C 6.3 10.8 6.3 10.2 6.5 9.5
            C 6.8 8.5 7.2 7.5 8 6.5 Z
            M 10 8.5
            C 10.5 7.5 11.5 6.8 12.5 6.3
            C 13.2 6 13.8 5.8 14.5 5.8
            C 15.2 5.8 15.8 6 16.5 6.3
            C 17.5 6.8 18.5 7.5 19 8.5
            C 19.5 9.5 19.8 10.5 20 11.5
            C 19.8 12.5 19.5 13.5 19 14.5
            C 18.5 15.2 17.5 15.7 16.5 16
            C 15.8 16.3 15.2 16.5 14.5 16.5
            C 13.8 16.5 13.2 16.3 12.5 16
            C 11.5 15.7 10.5 15.2 10 14.5
            C 9.5 13.5 9.2 12.5 9 11.5
            C 9.2 10.5 9.5 9.5 10 8.5 Z
        `,
        dentin: `
            M 11.5 9.5
            C 12 8.5 13 7.8 13.5 7.5
            C 14 7.3 14.2 7.2 14.5 7.2
            C 14.8 7.2 15 7.3 15.5 7.5
            C 16 7.8 17 8.5 17.5 9.5
            C 18 10.5 18.2 11.5 18.2 12.5
            C 18 13.5 17.5 14.5 17 15
            C 16.5 15.5 15.5 16 14.5 16
            C 13.5 16 12.5 15.5 12 15
            C 11.5 14.5 11 13.5 10.8 12.5
            C 10.8 11.5 11 10.5 11.5 9.5 Z
        `,
        pulp: `
            M 13 11
            C 13.3 10.5 13.7 10.3 14 10.2
            C 14.3 10.2 14.5 10.2 14.5 10.2
            C 14.5 10.2 14.7 10.2 15 10.2
            C 15.3 10.3 15.7 10.5 16 11
            C 16.2 11.5 16.3 12 16.3 12.5
            C 16.3 13 16.2 13.5 16 14
            C 15.7 14.3 15.3 14.5 15 14.5
            C 14.7 14.5 14.5 14.5 14.5 14.5
            C 14.5 14.5 14.3 14.5 14 14.5
            C 13.7 14.3 13.3 14 13 13.5
            C 12.8 13 12.7 12.5 12.7 12
            C 12.7 11.5 12.8 11 13 11 Z
        `,
        root: `
            M 12.5 19.3
            C 12.7 19.5 13 19.7 13.5 19.8
            C 13.7 20.5 13.8 21.5 13.7 22.5
            C 13.6 24 13.4 25.3 13.2 26.5
            C 13 27.2 12.8 27.5 12.5 27.8
            C 12.2 27.5 12 27.2 11.8 26.5
            C 11.6 25.3 11.4 24 11.3 22.5
            C 11.2 21.5 11.3 20.5 11.5 19.8
            C 12 19.7 12.3 19.5 12.5 19.3 Z
            M 15.5 19.3
            C 15.7 19.5 16 19.7 16.5 19.8
            C 16.7 20.5 16.8 21.5 16.7 22.5
            C 16.6 24 16.4 25.3 16.2 26.5
            C 16 27.2 15.8 27.5 15.5 27.8
            C 15.2 27.5 15 27.2 14.8 26.5
            C 14.6 25.3 14.4 24 14.3 22.5
            C 14.2 21.5 14.3 20.5 14.5 19.8
            C 15 19.7 15.3 19.5 15.5 19.3 Z
        `
    }
};

// Tooth position names for tooltips
export const TOOTH_NAMES = {
    // Adult teeth (FDI notation)
    11: { type: 'incisor', name: 'Upper Right Central Incisor', shortName: 'UR1' },
    12: { type: 'incisor', name: 'Upper Right Lateral Incisor', shortName: 'UR2' },
    13: { type: 'canine', name: 'Upper Right Canine', shortName: 'UR3' },
    14: { type: 'premolar', name: 'Upper Right First Premolar', shortName: 'UR4' },
    15: { type: 'premolar', name: 'Upper Right Second Premolar', shortName: 'UR5' },
    16: { type: 'molar', name: 'Upper Right First Molar', shortName: 'UR6' },
    17: { type: 'molar', name: 'Upper Right Second Molar', shortName: 'UR7' },
    18: { type: 'wisdomTooth', name: 'Upper Right Third Molar', shortName: 'UR8' },

    21: { type: 'incisor', name: 'Upper Left Central Incisor', shortName: 'UL1' },
    22: { type: 'incisor', name: 'Upper Left Lateral Incisor', shortName: 'UL2' },
    23: { type: 'canine', name: 'Upper Left Canine', shortName: 'UL3' },
    24: { type: 'premolar', name: 'Upper Left First Premolar', shortName: 'UL4' },
    25: { type: 'premolar', name: 'Upper Left Second Premolar', shortName: 'UL5' },
    26: { type: 'molar', name: 'Upper Left First Molar', shortName: 'UL6' },
    27: { type: 'molar', name: 'Upper Left Second Molar', shortName: 'UL7' },
    28: { type: 'wisdomTooth', name: 'Upper Left Third Molar', shortName: 'UL8' },

    31: { type: 'incisor', name: 'Lower Left Central Incisor', shortName: 'LL1' },
    32: { type: 'incisor', name: 'Lower Left Lateral Incisor', shortName: 'LL2' },
    33: { type: 'canine', name: 'Lower Left Canine', shortName: 'LL3' },
    34: { type: 'premolar', name: 'Lower Left First Premolar', shortName: 'LL4' },
    35: { type: 'premolar', name: 'Lower Left Second Premolar', shortName: 'LL5' },
    36: { type: 'molar', name: 'Lower Left First Molar', shortName: 'LL6' },
    37: { type: 'molar', name: 'Lower Left Second Molar', shortName: 'LL7' },
    38: { type: 'wisdomTooth', name: 'Lower Left Third Molar', shortName: 'LL8' },

    41: { type: 'incisor', name: 'Lower Right Central Incisor', shortName: 'LR1' },
    42: { type: 'incisor', name: 'Lower Right Lateral Incisor', shortName: 'LR2' },
    43: { type: 'canine', name: 'Lower Right Canine', shortName: 'LR3' },
    44: { type: 'premolar', name: 'Lower Right First Premolar', shortName: 'LR4' },
    45: { type: 'premolar', name: 'Lower Right Second Premolar', shortName: 'LR5' },
    46: { type: 'molar', name: 'Lower Right First Molar', shortName: 'LR6' },
    47: { type: 'molar', name: 'Lower Right Second Molar', shortName: 'LR7' },
    48: { type: 'wisdomTooth', name: 'Lower Right Third Molar', shortName: 'LR8' },

    // Child teeth (deciduous/primary)
    51: { type: 'incisor', name: 'Upper Right Primary Central Incisor', shortName: 'URA' },
    52: { type: 'incisor', name: 'Upper Right Primary Lateral Incisor', shortName: 'URB' },
    53: { type: 'canine', name: 'Upper Right Primary Canine', shortName: 'URC' },
    54: { type: 'molar', name: 'Upper Right Primary First Molar', shortName: 'URD' },
    55: { type: 'molar', name: 'Upper Right Primary Second Molar', shortName: 'URE' },

    61: { type: 'incisor', name: 'Upper Left Primary Central Incisor', shortName: 'ULA' },
    62: { type: 'incisor', name: 'Upper Left Primary Lateral Incisor', shortName: 'ULB' },
    63: { type: 'canine', name: 'Upper Left Primary Canine', shortName: 'ULC' },
    64: { type: 'molar', name: 'Upper Left Primary First Molar', shortName: 'ULD' },
    65: { type: 'molar', name: 'Upper Left Primary Second Molar', shortName: 'ULE' },

    71: { type: 'incisor', name: 'Lower Left Primary Central Incisor', shortName: 'LLA' },
    72: { type: 'incisor', name: 'Lower Left Primary Lateral Incisor', shortName: 'LLB' },
    73: { type: 'canine', name: 'Lower Left Primary Canine', shortName: 'LLC' },
    74: { type: 'molar', name: 'Lower Left Primary First Molar', shortName: 'LLD' },
    75: { type: 'molar', name: 'Lower Left Primary Second Molar', shortName: 'LLE' },

    81: { type: 'incisor', name: 'Lower Right Primary Central Incisor', shortName: 'LRA' },
    82: { type: 'incisor', name: 'Lower Right Primary Lateral Incisor', shortName: 'LRB' },
    83: { type: 'canine', name: 'Lower Right Primary Canine', shortName: 'LRC' },
    84: { type: 'molar', name: 'Lower Right Primary First Molar', shortName: 'LRD' },
    85: { type: 'molar', name: 'Lower Right Primary Second Molar', shortName: 'LRE' }
};

// Adult teeth layout (FDI notation)
export const ADULT_TEETH_LAYOUT = {
    upper: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
    lower: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38]
};

// Child teeth layout (FDI notation)
export const CHILD_TEETH_LAYOUT = {
    upper: [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
    lower: [85, 84, 83, 82, 81, 71, 72, 73, 74, 75]
};

// Helper function to get tooth metadata
export const getToothMetadata = (toothNumber) => {
    return TOOTH_NAMES[toothNumber] || { type: 'molar', name: `Tooth ${toothNumber}`, shortName: `T${toothNumber}` };
};

// Helper function to get tooth SVG path
export const getToothSVGPath = (toothNumber) => {
    const metadata = getToothMetadata(toothNumber);
    return TOOTH_SVG_PATHS[metadata.type] || TOOTH_SVG_PATHS.molar;
};

// Helper function to check if tooth number is valid
export const isValidToothNumber = (toothNumber) => {
    return TOOTH_NAMES.hasOwnProperty(toothNumber);
};

// Helper function to check if tooth number is for adult or child
export const isAdultTooth = (toothNumber) => {
    return toothNumber >= 11 && toothNumber <= 48;
};

export const isChildTooth = (toothNumber) => {
    return toothNumber >= 51 && toothNumber <= 85;
};
